/**
 * Shared sort helpers for roster/people dropdowns and typeaheads.
 *
 * Firestore roster reads (`useCollection('roster', ...)` / `useFormConfig()` in
 * `lib/useFirestore.js`) have no `orderBy` clause, so without this the display order is
 * raw snapshot order (roughly doc-ID / insertion order, since doc ids are
 * `email.replace(/[^a-z0-9]/g, '_')`) — effectively arbitrary. Users reported this looked
 * like it was sorting on the middle name/initial embedded in `displayName` (e.g.
 * "Davina F. Harden") purely by chance.
 *
 * `displayName` is normalized (see `normalizeName()` in this file's sibling `csv.js`) into
 * "First [Middle] Last" order from Aderant/SSRS's "LAST, FIRST [M.]" export order. For
 * sorting purposes we want first name and last name only — any middle initial or full
 * middle name in between must be ignored entirely, per explicit user request.
 */

/**
 * Extracts the first- and last-name sort keys from a person's `displayName`, ignoring
 * any middle name/initial in between. Both keys are lowercased for case-insensitive
 * comparison.
 *
 * Edge cases handled defensively (never throws):
 * - Empty/missing `displayName` -> `{ first: '', last: '' }`.
 * - Single-token name (no space) -> that token used for both `first` and `last`.
 *
 * @param {string} displayName
 * @returns {{ first: string, last: string }}
 */
export function personNameSortKey(displayName) {
  const trimmed = (displayName || '').trim();
  if (!trimmed) return { first: '', last: '' };

  const tokens = trimmed.split(/\s+/);
  const first = tokens[0].toLowerCase();
  const last = tokens[tokens.length - 1].toLowerCase();
  return { first, last };
}

/**
 * Comparator for `.sort()`, usable directly with roster-person objects (anything with a
 * `displayName` string). Compares first by first-name key, then by last-name key as a
 * tiebreaker, using `localeCompare` so apostrophes/hyphens (e.g. "O'Donnell",
 * "Zamora-Mejia") sort the way users expect.
 *
 * @param {{ displayName?: string }} a
 * @param {{ displayName?: string }} b
 * @returns {number}
 */
export function comparePeopleByName(a, b) {
  const keyA = personNameSortKey(a && a.displayName);
  const keyB = personNameSortKey(b && b.displayName);

  const firstCompare = keyA.first.localeCompare(keyB.first);
  if (firstCompare !== 0) return firstCompare;

  return keyA.last.localeCompare(keyB.last);
}

/**
 * Returns a new array of people sorted by first name, then last name (see
 * `comparePeopleByName`). Never mutates the input array.
 *
 * @param {Array<{ displayName?: string }>} people
 * @returns {Array<{ displayName?: string }>}
 */
export function sortPeople(people) {
  return [...(people || [])].sort(comparePeopleByName);
}
